import Vue from "vue";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { GMB_EXPORT_GROUP_BY } from "@/enums/gmbData.enum";
import { PERIOD } from "@/enums/period.enum";
import fileDownload from "js-file-download";
import METRICS from "@/enums/gmbMetricsUpdated.enum.js";

export class GMBWithComparisionDataBase {
  constructor() {
    this.gmbModelWithComparison = null;
    this.file = null;
  }

  async makeGmb(config, downloadFileExtension = null) {
    const apiPayload = this._createGMBPayload(config);

    if (downloadFileExtension === null) {
      // showing data, not downloading
      const apiComparisionPayload = this._createGMBPayload({
        dateFrom: config.compareDateFrom,
        dateTo: config.compareDateTo,
        googleImportedAddressesIds: config.googleImportedAddressesIds,
        metrics: config.metrics,
        group: config.group
      });
      const gmbDataFromApi = await this._getGMBDataFromApi(apiPayload);
      const gmbComparisionDataFromApi = await this._getGMBDataFromApi(
        apiComparisionPayload
      );

      this.gmbModelWithComparison = this._createGMBDataModel(
        gmbDataFromApi,
        gmbComparisionDataFromApi
      );
    } else {
      // GTM event data collection below
      Vue.gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ANALYTICS_EXPORT,
        format: downloadFileExtension,
        action:
          config.group === GMB_EXPORT_GROUP_BY.GMB_ADDRESS
            ? "export_by_locations"
            : "export_by_dates"
      });
      // end GTM event stuff

      this.file = await this._getExportedFileFromApi(apiPayload, {
        file_type: downloadFileExtension
      });
      fileDownload(
        this.file,
        `GMB_analysis_export_${config.dateFrom}_${config.dateTo}.${downloadFileExtension}`
      );
      this.file = null;
    }
  }

  async makeGmbAsync(config, downloadFileExtension = null) {
    const apiPayload = this._createGMBPayload(config);

    if (downloadFileExtension === null) {
      // showing data, not downloading
      const apiComparisionPayload = this._createGMBPayload({
        dateFrom: config.compareDateFrom,
        dateTo: config.compareDateTo,
        googleImportedAddressesIds: config.googleImportedAddressesIds,
        metrics: config.metrics,
        group: config.group
      });
      const gmbDataFromApi = await this._getGMBDataFromApi(apiPayload);
      const gmbComparisionDataFromApi = await this._getGMBDataFromApi(
        apiComparisionPayload
      );

      this.gmbModelWithComparison = this._createGMBDataModel(
        gmbDataFromApi,
        gmbComparisionDataFromApi
      );
    } else {
      // GTM event data collection below
      Vue.gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.GMB_ANALYTICS_EXPORT,
        format: downloadFileExtension,
        action:
          config.group === GMB_EXPORT_GROUP_BY.GMB_ADDRESS
            ? "export_by_locations"
            : "export_by_dates"
      });
      // end GTM event stuff

      return await this._getExportedFileFromApiAsync(apiPayload, {
        file_type: downloadFileExtension
      });
    }
  }

  returnGmb() {
    return this.gmbModelWithComparison;
  }

  _createGMBPayload({
    dateFrom,
    dateTo,
    googleImportedAddressesIds,
    metrics,
    group
  }) {
    const allMetrics = metrics.map(metric => {
      return {
        metric: metric,
        group_by: group
      };
    });

    return {
      date_from: dateFrom,
      date_to: dateTo || dateFrom,
      gmb_addresses: googleImportedAddressesIds,
      cities: [],
      metrics: allMetrics
    };
  }

  _createGMBDataModel(gmbDataFromApi, gmbComparisionDataFromApi) {
    const [combinedViewsStats, viewsSum] = this._combineViewsStats(
      gmbDataFromApi
    );
    const [_, comparisonViewsSum] = this._combineViewsStats(
      gmbComparisionDataFromApi
    );
    return gmbDataFromApi.map((gmbMetric, idx) => {
      const valuesSum = this._setGmbValuesSum(
        gmbMetric.values.map(value => value.value)
      );
      const compareValuesSum = this._setGmbValuesSum(
        gmbComparisionDataFromApi[idx].values.map(value => value.value)
      );
      return {
        groupBy: gmbMetric.group_by || PERIOD.DAY,
        metric: gmbMetric.metric || null,
        values: this._setGmbValues(gmbMetric.values),
        conversionValues: this._setGmbConversionValues(
          gmbMetric,
          combinedViewsStats
        ),
        sum: valuesSum,
        compareSum: compareValuesSum,
        periodPercentageDiff: this._setPercentageDiff(
          valuesSum,
          compareValuesSum
        ),
        conversionPeriodPercentageDiff: this._setConversionPercentageDiff(
          gmbMetric,
          viewsSum,
          valuesSum,
          comparisonViewsSum,
          compareValuesSum
        )
      };
    });
  }

  _setGmbValues(values) {
    return values.map(value => {
      return {
        t: value.date,
        y: value.value
      };
    });
  }

  _setGmbValuesSum(values) {
    return values.reduce((a, b) => a + b, 0);
  }

  _setPercentageDiff(sum, compareSum) {
    if (compareSum === 0) {
      return 100;
    }
    return ((sum / compareSum) * 100).toFixed(0) - 100;
  }

  _setConversionPercentageDiff(
    gmbMetric,
    viewsSum,
    sum,
    comparisonViewsSum,
    compareSum
  ) {
    if (!this.conversionMetric.includes(gmbMetric.metric)) {
      return null;
    }
    const conversion = viewsSum === 0 ? 100 : sum / viewsSum;
    const compareConversion =
      comparisonViewsSum === 0 ? 100 : compareSum / comparisonViewsSum;
    if (compareConversion === 0) {
      return 100;
    }
    return ((conversion / compareConversion) * 100).toFixed(0) - 100;
  }

  _combineViewsStats(gmbDataFromApi) {
    const viewMetrics = gmbDataFromApi.filter(gmbMetric =>
      this.viewsMetrics.includes(gmbMetric.metric)
    );
    const combinedStats = {};
    let sum = 0;
    viewMetrics.forEach(metric => {
      metric.values.forEach(value => {
        if (combinedStats[value.date]) {
          combinedStats[value.date] += value.value;
        } else {
          combinedStats[value.date] = value.value;
        }
        sum += value.value;
      });
    });
    return [combinedStats, sum];
  }

  _setGmbConversionValues(gmbMetric, combinedViewsStats) {
    if (!this.conversionMetric.includes(gmbMetric.metric)) {
      return null;
    }
    const conversionValues = gmbMetric.values.map(value => {
      const views = combinedViewsStats[value.date] || value.value;
      return {
        date: value.date,
        value: ((value.value / views) * 100).toFixed(1)
      };
    });

    return this._setGmbValues(conversionValues);
  }

  get viewsMetrics() {
    return [
      METRICS.BUSINESS_IMPRESSIONS_DESKTOP_MAPS,
      METRICS.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH,
      METRICS.BUSINESS_IMPRESSIONS_MOBILE_MAPS,
      METRICS.BUSINESS_IMPRESSIONS_MOBILE_SEARCH
    ];
  }

  get conversionMetric() {
    return [
      METRICS.BUSINESS_DIRECTION_REQUESTS,
      METRICS.CALL_CLICKS,
      METRICS.WEBSITE_CLICKS
    ];
  }
}
